import { observable, computed } from 'mobx'
import _ from 'underscore'
import { t } from 'i18next'

import { DBObject } from './DBObject'
import { Passage } from './Passage'
import { Project } from './Project'
import { IDB } from './IDB'
import { move, remove } from './Utils'
import { RefRange } from '../scrRefs/RefRange'
import { refRangesToDisplay } from '../scrRefs/Utils'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const log = require('debug')('sltt:SegmentPositionDialog')

export class Portion extends DBObject {
    @observable name = ''

    @observable rank = ''

    @observable _rev = 0

    @observable passages: Passage[]

    @observable references: RefRange[] = []

    @observable copiedFromId = '' // portion was copied from another portion. e.g. <projectName>/<_id>

    @computed get trackedProjectName() {
        if (this.copiedFromId.indexOf('/') >= 0) {
            return this.copiedFromId.slice(0, this.copiedFromId.indexOf('/'))
        }
        return ''
    }

    @computed get trackedPortionId() {
        return this.copiedFromId.slice(this.copiedFromId.indexOf('/') + 1)
    }

    constructor(_id: string, db: IDB) {
        super(_id, db)
        this.passages = []
    }

    toDocument() {
        const { name, rank, references, copiedFromId } = this
        const serializedReferences = JSON.stringify(references)
        return this._toDocument({ name, rank, references: serializedReferences, copiedFromId })
    }

    copy() {
        let copy = new Portion(this._id, this.db)
        copy = Object.assign(copy, this)
        copy.passages = this.passages.map((pas) => pas.copy())
        copy.references = this.references.map((ref) => ref.copy())
        return copy
    }

    toSnapshot() {
        return this.toDocument()
    }

    async setName(name: string) {
        if (name.trim() === this.name) {
            return
        }
        const doc = this._toDocument({ name })
        await this.db.put(doc)
    }

    async setRank(rankNumber: number) {
        const doc = this._toDocument({ rank: DBObject.numberToRank(rankNumber) })
        await this.db.put(doc)
    }

    createPassage(name: string) {
        name = name.trim()
        if (this.passages.find((p) => p.name === name)) {
            throw Error(t('Duplicate name'))
        }

        const newId = this.db.getNewId(this.passages, new Date(Date.now()))
        const passage = new Passage(`${this._id}/${newId}`, this.db)

        passage.name = name

        let _rank = 100
        if (this.passages.length > 0) {
            _rank = this.passages.slice(-1)[0].rankAsNumber + 100
        }
        passage.rank = DBObject.numberToRank(_rank)

        return passage
    }

    createPassageFromExisting(passage: Passage) {
        let { name } = passage
        let number = 0
        while (this.passages.find((p) => p.name === name)) {
            number++
            name = `${passage.name}-${number}`
        }
        const newPassage = this.createPassage(name)
        const copy = passage.copy()
        copy._id = newPassage._id
        copy.name = newPassage.name
        copy.rank = newPassage.rank
        copy.documents = []
        copy.oralResources = []
        copy.pdfs = []
        copy.videos = []
        return copy
    }

    async addPassageFromExisting(passage: Passage) {
        await this.db.put(passage.toDocument())

        const _passage = _.findWhere(this.passages, { _id: passage._id })
        if (!_passage) throw Error('could not find newly created Passage')
        return _passage
    }

    async addPassage(name: string) {
        const passage = this.createPassage(name)
        await this.db.put(passage.toDocument())
        return _.findWhere(this.passages, { _id: passage._id })
    }

    videod() {
        return this.passages.some((passage) => passage.videod())
    }

    async removePassage(_id: string) {
        await remove(this.passages, _id)
    }

    async movePassage(_id: string, i: number) {
        const idx = _.findIndex(this.passages, { _id })
        if (idx === i) {
            log('movePassage nothing to do')
            return /* nothing to do */
        }

        if (idx === -1) throw Error(`movePassage: _id not found [${_id}]`)

        await move(this.passages, idx, i)
    }

    // Get a passage matching the _id or the first passage if no match or null
    getDefaultPassage(_id: string | null) {
        const { passages } = this
        let passage = _id && _.findWhere(passages, { _id })

        if (!passage) {
            if (passages.length > 0) passage = passages[0]
        }

        return passage || null
    }

    checkNewPassageName(name: string) {
        name = name.trim()
        if (_.findWhere(this.passages, { name })) {
            return t('Duplicate name')
        }
        if (name === '') {
            return t('Empty name')
        }

        return ''
    }

    async setReferences(refs: RefRange[]) {
        const serializedReferences = JSON.stringify(refs)
        if (JSON.stringify(this.references) === serializedReferences) {
            return
        }

        const doc = this._toDocument({ references: serializedReferences })
        await this.db.put(doc)
    }

    unviewedVideoExists(username: string, cutoff: Date) {
        const unviewedVideos = this.passages
            .map((passage) => passage.firstUnviewedVideo(username, cutoff))
            .filter((video) => video !== null)
        return unviewedVideos.length > 0
    }

    unviewedNoteExists(username: string, cutoff: Date, includeConsultantOnlyNotes: boolean) {
        const unviewedNotes = this.passages
            .map((passage) => passage.firstUnviewedNote(username, cutoff, includeConsultantOnlyNotes))
            .filter((note) => note !== null)
        return unviewedNotes.length > 0
    }

    unresolvedNoteExists(cutoff: Date, includeConsultantOnlyNotes: boolean) {
        const unresolvedNotes = this.passages
            .map((passage) => passage.firstUnresolvedNoteOnLatestVideo(cutoff, includeConsultantOnlyNotes))
            .filter((note) => note !== null)
        return unresolvedNotes.length > 0
    }

    getTranscription(project: Project) {
        let transcription = this.references.length ? `|${refRangesToDisplay(this.references, project)}|\n` : ''

        for (const passage of this.passages.filter((item) => item.videosNotDeleted.length)) {
            const passageTranscription = passage.videosNotDeleted.slice(-1)[0].getTranscription(passage, project)
            if (passageTranscription) {
                transcription = `${transcription}${passageTranscription}\n`
            }
        }

        return transcription.trim()
    }
}
